'use client';

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

export type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  icon?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const VARIANTS: Record<
  AlertVariant,
  { container: string; iconColor: string; defaultIcon: ReactNode; role: 'alert' | 'status' }
> = {
  info: {
    container: 'bg-purple-50 border-purple-500/20 text-purple-800',
    iconColor: 'text-purple-600',
    defaultIcon: <Info className="h-[18px] w-[18px]" strokeWidth={2} />,
    role: 'status',
  },
  success: {
    container: 'bg-success-50 border-success-500/30 text-success-700',
    iconColor: 'text-success-700',
    defaultIcon: <CheckCircle2 className="h-[18px] w-[18px]" strokeWidth={2} />,
    role: 'status',
  },
  warning: {
    container: 'bg-warning-50 border-warning-500/30 text-warning-700',
    iconColor: 'text-warning-700',
    defaultIcon: <AlertTriangle className="h-[18px] w-[18px]" strokeWidth={2} />,
    role: 'alert',
  },
  danger: {
    container: 'bg-danger-50 border-danger-500/30 text-danger-700',
    iconColor: 'text-danger-700',
    defaultIcon: <XCircle className="h-[18px] w-[18px]" strokeWidth={2} />,
    role: 'alert',
  },
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { variant = 'info', icon, onDismiss, dismissLabel, className, children, role, ...props },
  ref,
) {
  const v = VARIANTS[variant];
  return (
    <div
      ref={ref}
      role={role ?? v.role}
      className={cn(
        'flex items-start gap-3 rounded-[10px] border px-4 py-3 text-[13px] leading-[1.5]',
        v.container,
        className,
      )}
      {...props}
    >
      <span className={cn('mt-px flex-shrink-0', v.iconColor)} aria-hidden>
        {icon ?? v.defaultIcon}
      </span>
      <div className="flex-1 [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2 [&_b]:font-semibold [&_strong]:font-semibold">
        {children}
      </div>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="text-fg-4 hover:text-fg-1 -mt-0.5 -mr-1 inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded transition-colors hover:bg-black/5"
        >
          <X className="h-3 w-3" strokeWidth={2.5} />
        </button>
      ) : null}
    </div>
  );
});
